/**
 * Lead Inbox — the review step between a lead source and the CRM.
 *
 * Nothing on this page sends a message, spends a credit, or calls an external
 * provider. It reads a queue and records decisions; Approve is the only action
 * that creates a contact, and it does so through one database transaction.
 */

import { useState } from 'react';
import { useDebounce } from 'use-debounce';
import { formatDistanceToNow } from 'date-fns';
import { CheckCircle2, Clock, Inbox, MessageSquare, RefreshCw, Upload, XCircle } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  BROWSABLE_STATES,
  useLeadInbox,
  useLeadInboxCounts,
  type BrowsableState,
} from '@/hooks/crm/useLeadInbox';
import {
  LEAD_INBOX_SOURCE_LABELS,
  REVIEW_STATE_LABELS,
  leadDisplayName,
  parseSelectionReasons,
  type LeadInboxRow,
  type LeadInboxSource,
} from '@/lib/crm/lead-inbox';
import { cn } from '@/lib/utils';
import { ImportDialog } from './ImportDialog';
import { ReviewDialog, type ReviewDecision } from './ReviewDialog';
import { SavetnikTranscriptDrawer } from './SavetnikTranscriptDrawer';

const SEARCH_DEBOUNCE_MS = 300;

const STATE_DESCRIPTIONS: Record<BrowsableState, string> = {
  pending: 'No leads are waiting for review. Import a CSV to fill the queue.',
  snoozed: 'Nothing is snoozed.',
  approved: 'No lead has been approved yet.',
  rejected: 'No lead has been rejected yet.',
};

const relativeTime = (value: string | null): string => {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Unknown time';
  return formatDistanceToNow(date, { addSuffix: true });
};

const sourceLabel = (source: LeadInboxSource): string =>
  LEAD_INBOX_SOURCE_LABELS[source] ?? source;

export default function LeadInbox(): JSX.Element {
  useDocumentTitle('Lead Inbox — Tacit');

  const [state, setState] = useState<BrowsableState>('pending');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);
  const [importing, setImporting] = useState(false);
  const [review, setReview] = useState<{ lead: LeadInboxRow; decision: ReviewDecision } | null>(null);
  const [transcriptSourceRef, setTranscriptSourceRef] = useState<string | null>(null);

  const leads = useLeadInbox(state, debouncedSearch);
  const counts = useLeadInboxCounts();
  const rows = leads.data ?? [];

  return (
    <Layout>
      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <Inbox className="h-6 w-6 text-primary" aria-hidden="true" />
                <h1 className="text-2xl font-semibold tracking-tight">Lead Inbox</h1>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                Every lead waits here for your decision. Approving one creates a CRM contact;
                nothing else does.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="min-h-11 md:min-h-11"
                onClick={() => void leads.refetch()}
                disabled={leads.isFetching}
              >
                <RefreshCw className={cn('mr-2 h-4 w-4', leads.isFetching && 'animate-spin')} aria-hidden="true" />
                Refresh
              </Button>
              <Button className="min-h-11 md:min-h-11" onClick={() => setImporting(true)}>
                <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
                Import CSV
              </Button>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {BROWSABLE_STATES.map((value) => (
              <Card key={value} className={cn(value === state && 'border-primary/50')}>
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {REVIEW_STATE_LABELS[value]}
                  </p>
                  <p className="mt-1 text-2xl font-semibold">
                    {counts.isLoading ? '—' : counts.data?.[value] ?? 0}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="text-base">Review queue</CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="w-56">
                    <Input
                      aria-label="Search leads by name, company, or email"
                      placeholder="Search name, company, or email"
                      value={searchInput}
                      onChange={(event) => setSearchInput(event.target.value)}
                    />
                  </div>
                  <div className="w-44">
                    <NativeSelect
                      aria-label="Review state filter"
                      value={state}
                      onChange={(event) => setState(event.target.value as BrowsableState)}
                    >
                      {BROWSABLE_STATES.map((value) => (
                        <option key={value} value={value}>
                          {REVIEW_STATE_LABELS[value]}
                        </option>
                      ))}
                    </NativeSelect>
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-0">
              <span id="lead-approve-action-label" className="sr-only">Approve and create a contact for</span>
              <span id="lead-snooze-action-label" className="sr-only">Snooze</span>
              <span id="lead-reject-action-label" className="sr-only">Reject</span>

              {leads.isLoading ? (
                <div className="space-y-2 p-4" role="status" aria-live="polite" aria-busy="true">
                  <span className="sr-only">Loading the lead queue</span>
                  {[1, 2, 3, 4].map((item) => <Skeleton key={item} className="h-16 w-full" />)}
                </div>
              ) : leads.isError ? (
                <div className="p-8 text-center" role="alert" aria-live="assertive">
                  <XCircle className="mx-auto h-8 w-8 text-destructive" aria-hidden="true" />
                  <p className="mt-3 font-medium">The lead queue could not be loaded</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    No lead was changed. Use Refresh to try again.
                  </p>
                </div>
              ) : rows.length === 0 ? (
                <div className="p-10 text-center" role="status" aria-live="polite">
                  <Inbox className="mx-auto h-9 w-9 text-muted-foreground" aria-hidden="true" />
                  <p className="mt-3 font-medium">
                    {debouncedSearch ? 'No lead matches this search' : STATE_DESCRIPTIONS[state]}
                  </p>
                </div>
              ) : (
                <Table className="block lg:table" aria-label="Leads awaiting review">
                  <TableHeader className="hidden lg:table-header-group">
                    <TableRow>
                      <TableHead>Lead</TableHead>
                      <TableHead>Why selected</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Added</TableHead>
                      <TableHead className="text-right">Decision</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="block lg:table-row-group">
                    {rows.map((lead) => (
                      <TableRow key={lead.id} className="grid gap-4 p-4 lg:table-row lg:p-0">
                        <TableCell
                          data-label="Lead"
                          className="grid min-w-0 grid-cols-[6.5rem_minmax(0,1fr)] gap-2 p-0 before:text-xs before:font-medium before:text-muted-foreground before:content-[attr(data-label)] lg:table-cell lg:p-2 lg:before:hidden"
                        >
                          <span className="sr-only lg:hidden">Lead: </span>
                          <div className="min-w-0">
                            <p
                              id={`lead-name-${lead.id}`}
                              className="break-words font-medium"
                              data-content-origin="external"
                            >
                              {leadDisplayName(lead)}
                            </p>
                            <p
                              className="break-words text-xs text-muted-foreground lg:max-w-64"
                              data-content-origin="external"
                            >
                              {[lead.job_title, lead.company_name].filter(Boolean).join(' · ') || '—'}
                            </p>
                            {lead.email && (
                              <p
                                className="break-all text-xs text-muted-foreground lg:max-w-64"
                                data-content-origin="external"
                              >
                                {lead.email}
                              </p>
                            )}
                          </div>
                        </TableCell>

                        <TableCell
                          data-label="Why selected"
                          className="grid min-w-0 grid-cols-[6.5rem_minmax(0,1fr)] gap-2 p-0 before:text-xs before:font-medium before:text-muted-foreground before:content-[attr(data-label)] lg:table-cell lg:p-2 lg:before:hidden"
                        >
                          <span className="sr-only lg:hidden">Why selected: </span>
                          <div className="flex min-w-0 flex-wrap items-center gap-1">
                            <Badge variant="outline">{sourceLabel(lead.source)}</Badge>
                            {lead.source === 'savetnik' && lead.source_ref && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="min-h-11 md:min-h-11"
                                onClick={() => setTranscriptSourceRef(lead.source_ref)}
                              >
                                <MessageSquare className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                                Razgovor
                              </Button>
                            )}
                            {parseSelectionReasons(lead.selection_reasons)
                              .filter((reason) => reason.kind !== 'source')
                              .map((reason) => (
                                <Badge
                                  key={`${reason.kind}-${reason.label}`}
                                  variant="outline"
                                  className="max-w-full break-words text-muted-foreground"
                                  data-content-origin="external"
                                >
                                  {reason.label}
                                </Badge>
                              ))}
                          </div>
                        </TableCell>

                        <TableCell
                          data-label="Score"
                          className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2 p-0 before:text-xs before:font-medium before:text-muted-foreground before:content-[attr(data-label)] lg:table-cell lg:p-2 lg:before:hidden"
                        >
                          <span className="sr-only lg:hidden">Score: </span>
                          <div>
                            {lead.lead_score === null ? (
                              <span className="text-xs text-muted-foreground">Not scored</span>
                            ) : (
                              <Badge
                                variant="outline"
                                className={lead.lead_score >= 80
                                  ? 'border-status-danger/40 bg-status-danger/10 text-status-danger'
                                  : 'border-status-warning/40 bg-status-warning/10 text-status-warning'}
                              >
                                {lead.lead_score}
                              </Badge>
                            )}
                          </div>
                        </TableCell>

                        <TableCell
                          data-label="Added"
                          className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2 whitespace-nowrap p-0 text-xs text-muted-foreground before:text-xs before:font-medium before:text-muted-foreground before:content-[attr(data-label)] lg:table-cell lg:p-2 lg:before:hidden"
                        >
                          <span className="sr-only lg:hidden">Added: </span>
                          <div>
                            {relativeTime(lead.created_at)}
                            {lead.review_state === 'snoozed' && lead.snoozed_until && (
                              <p className="mt-1 text-[11px]">
                                Back {relativeTime(lead.snoozed_until)}
                              </p>
                            )}
                          </div>
                        </TableCell>

                        <TableCell
                          data-label="Decision"
                          className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2 p-0 before:text-xs before:font-medium before:text-muted-foreground before:content-[attr(data-label)] lg:table-cell lg:p-2 lg:before:hidden"
                        >
                          <span className="sr-only lg:hidden">Decision: </span>
                          {lead.review_state === 'pending' || lead.review_state === 'snoozed' ? (
                            <div className="flex flex-wrap justify-start gap-2 lg:justify-end lg:gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="min-h-11 md:min-h-11"
                                aria-labelledby={`lead-approve-action-label lead-name-${lead.id}`}
                                onClick={() => setReview({ lead, decision: 'approve' })}
                              >
                                <CheckCircle2 className="mr-1 h-4 w-4 text-status-success" aria-hidden="true" />
                                Approve
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
                                aria-labelledby={`lead-snooze-action-label lead-name-${lead.id}`}
                                title="Snooze"
                                onClick={() => setReview({ lead, decision: 'snooze' })}
                              >
                                <Clock className="h-4 w-4" aria-hidden="true" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
                                aria-labelledby={`lead-reject-action-label lead-name-${lead.id}`}
                                title="Reject"
                                onClick={() => setReview({ lead, decision: 'reject' })}
                              >
                                <XCircle className="h-4 w-4" aria-hidden="true" />
                              </Button>
                            </div>
                          ) : (
                            <div className="lg:text-right">
                              <Badge variant="outline">{REVIEW_STATE_LABELS[lead.review_state]}</Badge>
                              {lead.promoted_contact_id && (
                                <a
                                  className="mt-1 block text-xs text-primary underline underline-offset-2"
                                  href={`/crm/contact/${lead.promoted_contact_id}`}
                                >
                                  Open contact
                                </a>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {importing && <ImportDialog onClose={() => setImporting(false)} />}
      {review && (
        <ReviewDialog
          key={`${review.lead.id}-${review.decision}`}
          lead={review.lead}
          decision={review.decision}
          onClose={() => setReview(null)}
        />
      )}
      {transcriptSourceRef && (
        <SavetnikTranscriptDrawer
          sourceRef={transcriptSourceRef}
          onClose={() => setTranscriptSourceRef(null)}
        />
      )}
    </Layout>
  );
}
